import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { useCustomerAnalytics } from "../../../services/hooks/useCustomerAnalytics";
import { useAppSelector } from "../../../services/hooks/useReduxHooks";
import { useTranslationI18 } from "../../../services/hooks/useTranslationI18";
import { logClientError } from "../../../utils/APICalls/APICalls";
import { AlertErrorType } from "../../../utils/ErrorTypes";
import LS from "../../../utils/LocalStorage";
import {
  CUSTOMER_ANALYTICS_EVENT,
  LocalStorageEnum,
} from "../../../utils/enums";
import {
  isServedLocale,
  switchLocale,
} from "../../../utils/help/localization/localization";
import { handleRedirectToHomepagePartnerApp } from "../../../utils/help/navigation/navigation";
import { PreferredCountryEnum } from "../../../utils/types";
import { BackArrowIcon } from "../Icons/Icons";
import styles from "./LangSwitch.module.scss";

export const LangSwitch = (props: {
  preferredCountry?: string;
  withMargin?: boolean;
  /** Fired with the newly-selected language flag when the user toggles language. */
  onToggle?: (lang: string) => void;
}) => {
  const router = useRouter();
  const addCustomerAnalytics = useCustomerAnalytics();

  const { t } = useTranslationI18();
  const [langs, setLangs] = useState<{ flag: string; langName: string }[]>([]);

  const language = useAppSelector((store) => store.config.selectedLanguage);
  const preferredCountry = useAppSelector(
    (store) => store.config.restaurantInfo?.preferredCountry
  );

  const secondLanguage = props.preferredCountry || preferredCountry;

  const displayPartnersBackButton =
    LS._get(LocalStorageEnum.MOBILE_APP_PARTNER) ||
    LS._get(LocalStorageEnum.HAS_BACK_BUTTON);

  const organizationCustomer = useAppSelector(
    (state) => state.partnerAccount.organizationCustomer
  );

  useEffect(() => {
    setLangs(getLanguagesFromCountry(secondLanguage as PreferredCountryEnum));
  }, [preferredCountry]);

  // Only starts the route change. The language, currency, LANG and direction
  // follow router.locale (useLocaleSync), so a switch another navigation
  // cancels never leaves the page half-switched (prepits/project#4381, point 4).
  const toggleLang = (e: any) => {
    // A locale this build does not serve has no page to switch to. Raise it
    // rather than a switch that quietly goes nowhere.
    if (!isServedLocale(router, e)) {
      logClientError(`${AlertErrorType.Language_switch_to_unserved_locale}`, {
        key: e,
        page: router.pathname,
        cause: router.locales?.join(","),
      });

      return;
    }

    addCustomerAnalytics(CUSTOMER_ANALYTICS_EVENT.CHANGE_LANGUAGE, {
      chosenLang: e,
    });

    props.onToggle?.(e);

    switchLocale(e);
  };

  const PartnerBackButton = () => {
    return displayPartnersBackButton ? (
      <button
        data-testid={"partner-back"}
        onClick={() => handleRedirectToHomepagePartnerApp(organizationCustomer)}
        className={styles.back_button}
      >
        <BackArrowIcon />
        Back
      </button>
    ) : (
      <></>
    );
  };

  return (
    <div className={styles.lang_buttons}>
      <PartnerBackButton />
      <div
        className={`${styles.btns_wrapper} ${
          props.withMargin ? styles.margin_inline : ""
        }`}
      >
        {language &&
          langs.map((lang: any, i: number) => {
            return (
              <button
                key={i}
                data-testid={"lang-switch-" + lang.flag}
                aria-label={lang.langName}
                onClick={() => toggleLang(lang.flag)}
                className={`${
                  (language === lang.flag && language !== undefined
                    ? styles.active
                    : "") +
                  " " +
                  styles.lang_btn
                } ${
                  lang.flag === "ar-SA" && lang.langName === "Arabic"
                    ? styles.ar_sa_lang_btn
                    : ""
                }`}
              >
                {t(`common:${lang.langName}`)}
              </button>
            );
          })}
      </div>
    </div>
  );
};

export const getLanguagesFromCountry = (country: PreferredCountryEnum) => {
  return [
    {
      flag: "en",
      langName: "English",
    },

    country === PreferredCountryEnum.CA
      ? {
          flag: "fr",
          langName: "French",
        }
      : {
          flag:
            country === PreferredCountryEnum.SA ||
            country === PreferredCountryEnum.KW ||
            country === PreferredCountryEnum.BH
              ? "ar-SA"
              : "ar",
          langName: "Arabic",
        },
  ];
};
